// Phase 4c — `(cartesian …)` tile op.
//
// `(cartesian :span [W H] :x-kind? :stack? :orientation?
//   (series …)+ (reference-line …)* (reference-area …)* (band …)*
//   (x-axis …)? (y-axis …)*)`
//
// Positional children dispatch through `CARTESIAN_CHILD_TABLE`
// (context-restricted to series / reference-line / reference-area /
// band / x-axis / y-axis);
// at least one `series` is required. kwargs default to
// time / none / vertical. Registered in `dashboardMeta` (NOT `opMeta`).

import { z } from 'zod';

import { ShapeError } from '../../../errors.ts';
import { type AstInput, opCallPos } from '../../../normalize.ts';
import { paramPath } from '../../../path.ts';
import { spanArg } from '../args.ts';
import { type DashboardCtx, dashboardMeta, dispatchDashboardChild } from '../dispatch.ts';
import { ORIENTATIONS, STACKS, X_KINDS } from '../enums.ts';
import { CARTESIAN_CHILD_TABLE, cartesianChildScope } from '../sub-forms.ts';
import type {
  BandSpec,
  CartesianSpec,
  ReferenceAreaSpec,
  ReferenceLineSpec,
  SeriesSpec,
  XAxisSpec,
  YAxisSpec,
} from '../value.ts';

export const cartesian = z
  .object({
    name: z.literal('cartesian'),
    params: z.array(z.unknown()).min(1),
    span: spanArg,
    'x-kind': z.enum(X_KINDS).default('time'),
    stack: z.enum(STACKS).default('none'),
    orientation: z.enum(ORIENTATIONS).default('vertical'),
  })
  .describe(
    'A line/bar/area/dot chart. Positional children: `(series …)` (≥1), optional ' +
      '`(reference-line …)` / `(reference-area …)` / `(band …)` / `(x-axis …)` / `(y-axis …)`. ' +
      '`:x-kind` time|category|numeric (default time).',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    childScopes: [cartesianChildScope],
    example: {
      expr: '(cartesian (series :type line :id r :label "Rev" :color blue :x (col w t) :y (col w r)) :span [12 6])',
    },
    apply: async (data, ctx: DashboardCtx): Promise<CartesianSpec> => {
      const children = await Promise.all(
        data.params.map((node, i) =>
          dispatchDashboardChild(node as AstInput, CARTESIAN_CHILD_TABLE, ctx, paramPath(ctx.path, i)),
        ),
      );
      const series = children.filter((c): c is SeriesSpec => c.kind === 'series-spec');
      const referenceLines = children.filter((c): c is ReferenceLineSpec => c.kind === 'reference-line-spec');
      const referenceAreas = children.filter((c): c is ReferenceAreaSpec => c.kind === 'reference-area-spec');
      const bands = children.filter((c): c is BandSpec => c.kind === 'band-spec');
      // Last spec per axis slot wins (a later child overrides an earlier one).
      const xAxisSpec = children.filter((c): c is XAxisSpec => c.kind === 'x-axis-spec').at(-1);
      const yAxes = children.filter((c): c is YAxisSpec => c.kind === 'y-axis-spec');
      const yLeft = yAxes.filter((a) => a.side === 'left').at(-1);
      const yRight = yAxes.filter((a) => a.side === 'right').at(-1);

      if (series.length === 0) {
        throw new ShapeError('(cartesian …) requires at least one (series …) child', opCallPos(ctx.spans, ctx.path));
      }

      return {
        kind: 'cartesian-spec',
        span: data.span,
        xKind: data['x-kind'],
        stack: data.stack,
        orientation: data.orientation,
        series,
        referenceLines,
        referenceAreas,
        bands,
        ...(xAxisSpec?.label === undefined ? {} : { xLabel: xAxisSpec.label }),
        ...(yLeft?.label === undefined ? {} : { yLabelLeft: yLeft.label }),
        ...(yRight?.label === undefined ? {} : { yLabelRight: yRight.label }),
      };
    },
  });
